import { ApiError } from '@anm/api';
import {
  CreateProjectParams,
  DeleteProjectParams,
  DuplicateProjectParams,
  EmbedInfo,
  MoveProjectParams,
  MoveProjectToTeamParams,
  MoveProjectToUserParams,
  UpdateProjectMeta
} from '@anm/api/modules/projects';
import {
  CreateFolderParams,
  DeleteFolderParams,
  Folder,
  GetByPathParams,
  GetTreeParams,
  MoveFolderParams,
  RenameFolderParams
} from '@anm/api/modules/videoFolder';
import { createAction, createAsyncAction } from '@anm/helpers/saga/typesafe-actions';
import { AnimatronProject } from 'project';

import {
  DuplicateProjectRequestParams,
  ExpandFolderInTreeParams,
  UpdateCreatedProject,
  UpdateProjectPreview
} from './types';

type CreateFolderRequestParams = Pick<CreateFolderParams, Exclude<keyof CreateFolderParams, 'name'>> & {
  name?: string;
};

export const createFolder = createAction('wave.video/videoFolder/CREATE')<CreateFolderRequestParams>();
export const createFolderAsync = createAsyncAction(
  'wave.video/videoFolder/CREATE_REQUEST',
  'wave.video/videoFolder/CREATE_SUCCESS',
  'wave.video/videoFolder/CREATE_ERROR'
)<CreateFolderParams, Folder, ApiError>();

export const fetchRootFolder = createAction('wave.video/videoFolder/FETCH_ROOT')<boolean | void>();
type FetchFolderByPathProps = {
  path: string;
  isForceLoad: boolean;
};

export const fetchFolderById = createAction('wave.video/videoFolder/FETCH_FOLDER_BY_ID')<number>();
export const fetchFolderByIdAsync = createAsyncAction(
  'wave.video/videoFolder/FETCH_FOLDER_BY_ID_REQUEST',
  'wave.video/videoFolder/FETCH_FOLDER_BY_ID_SUCCESS',
  'wave.video/videoFolder/FETCH_FOLDER_BY_ID_ERROR'
)<number, Folder, ApiError>();

export const fetchFolderByPath = createAction('wave.video/videoFolder/FETCH_PATH')<FetchFolderByPathProps>();
export const fetchAsync = createAsyncAction(
  'wave.video/videoFolder/FETCH_REQUEST',
  'wave.video/videoFolder/FETCH_SUCCESS',
  'wave.video/videoFolder/FETCH_ERROR'
)<GetByPathParams, Folder, ApiError>();

export const expandFolderInTree = createAction('wave.video/videoFolder/EXPAND_FOLDER_IN_TREE')<
  ExpandFolderInTreeParams
>();
export const fetchFolderTree = createAction('wave.video/videoFolder/FETCH_FOLDER_TREE')<string>();
export const fetchFolderTreeAsync = createAsyncAction(
  'wave.video/videoFolder/FETCH_FOLDER_TREE_REQUEST',
  'wave.video/videoFolder/FETCH_FOLDER_TREE_SUCCESS',
  'wave.video/videoFolder/FETCH_FOLDER_TREE_ERROR'
)<GetTreeParams, Folder, ApiError>();

export const moveFolder = createAction('wave.video/videoFolder/MOVE')<MoveFolderParams>();
export const moveFolderAsync = createAsyncAction(
  'wave.video/videoFolder/MOVE_REQUEST',
  'wave.video/videoFolder/MOVE_SUCCESS',
  'wave.video/videoFolder/MOVE_ERROR'
)<MoveFolderParams, string, ApiError>();

export const renameFolder = createAction('wave.video/videoFolder/RENAME')<RenameFolderParams>();
export const renameFolderAsync = createAsyncAction(
  'wave.video/videoFolder/RENAME_REQUEST',
  'wave.video/videoFolder/RENAME_SUCCESS',
  'wave.video/videoFolder/RENAME_ERROR'
)<RenameFolderParams, Folder, ApiError>();

export const deleteFolder = createAction('wave.video/videoFolder/DELETE')<DeleteFolderParams>();
export const deleteFolderAsync = createAsyncAction(
  'wave.video/videoFolder/DELETE_REQUEST',
  'wave.video/videoFolder/DELETE_SUCCESS',
  'wave.video/videoFolder/DELETE_ERROR'
)<DeleteFolderParams, string, ApiError>();

export const moveProject = createAction('wave.video/project/MOVE')<MoveProjectParams>();
export const moveProjectAsync = createAsyncAction(
  'wave.video/project/MOVE_REQUEST',
  'wave.video/project/MOVE_SUCCESS',
  'wave.video/project/MOVE_ERROR'
)<MoveProjectParams, string, ApiError>();

export const deleteProject = createAction('wave.video/project/DELETE')<string>();
export const deleteProjectAsync = createAsyncAction(
  'wave.video/project/DELETE_REQUEST',
  'wave.video/project/DELETE_SUCCESS',
  'wave.video/project/DELETE_ERROR'
)<DeleteProjectParams, string, ApiError>();

export const clearDeleteProject = createAction('wave.video/project/CLEAR_DELETE')();

export const updateProjectMeta = createAction('wave.video/project/UPDATE_PROJECT_META')<UpdateProjectMeta>();
export const updateProjectMetaAsync = createAsyncAction(
  'wave.video/project/UPDATE_PROJECT_META_REQUEST',
  'wave.video/project/UPDATE_PROJECT_META_SUCCESS',
  'wave.video/project/UPDATE_PROJECT_META_ERROR'
)<UpdateProjectMeta, string, ApiError>();

export const updateProjectAllStatuses = createAction('wave.video/project/UPDATE_ALL_STATUSES')();

type UpdateProject = Partial<Pick<AnimatronProject, Exclude<keyof AnimatronProject, 'id'>>> & { id: string };
export const updateProjectAsync = createAsyncAction(
  'wave.video/project/UPDATE_REQUEST',
  'wave.video/project/UPDATE_SUCCESS',
  'wave.video/project/UPDATE_ERROR'
)<Partial<AnimatronProject>[], UpdateProject[], ApiError>();

export const duplicateProject = createAction('wave.video/project/DUPLICATE')<DuplicateProjectRequestParams>();
export const duplicateProjectAsync = createAsyncAction(
  'wave.video/project/DUPLICATE_REQUEST',
  'wave.video/project/DUPLICATE_SUCCESS',
  'wave.video/project/DUPLICATE_ERROR'
)<DuplicateProjectParams, AnimatronProject, ApiError>();

export const duplicateAndOpenEditorProject = createAction('wave.video/project/DUPLICATE_AND_OPEN_EDITOR')<
  DuplicateProjectRequestParams
>();

export const clearNewFolder = createAction('wave.video/videoFolder/CLEAR_NEW_FOLDER')();

export const clearCreatedProject = createAction('wave.video/project/CLEAR_CREATED_PROJECT')();

export const createProject = createAction('wave.video/project/CREATE_PROJECT')();

export const addProject = createAction('wave.video/project/ADD_NEW_PROJECT')<AnimatronProject>();

export const updateCreatedProject = createAction('wave.video/project/UPDATE_CREATED_PROJECT')<UpdateCreatedProject>();

export const updateProjectHostingId = createAction('wave.video/project/UPDATE_PROJECT_HOSTING_ID')<{
  hostId: string;
  projectId: string;
}>();

export const cancelCreateProject = createAction('wave.video/project/CANCEL_CREATE_PROJECT')<string>();

export const createProjectAsync = createAsyncAction(
  'wave.video/project/CREATE_PROJECT_REQUEST',
  'wave.video/project/CREATE_PROJECT_SUCCESS',
  'wave.video/project/CREATE_PROJECT_FAILURE'
)<CreateProjectParams, AnimatronProject, ApiError>();

export const updateProjectPreview = createAction('wave.video/project/UPDATE_PROJECT_PREVIEW')<UpdateProjectPreview>();

export const updateEmbedStatus = createAction('wave.video/project/UPDATE_EMBED_STATUS')<EmbedInfo>();

export const removeProjectFromList = createAction('wave.video/project/REMOVE_PROJECT_FROM_LIST')<string>();

export const moveProjectToUser = createAction('wave.video/project/MOVE_PROJECT_TO_USER_WORKSPACE')<
  MoveProjectToUserParams
>();
export const moveProjectToTeam = createAction('wave.video/project/MOVE_PROJECT_TO_TEAM_WORKSPACE')<
  MoveProjectToTeamParams
>();
export const moveProjectToOtherAsync = createAsyncAction(
  'wave.video/project/MOVE_PROJECT_TO_OTHER_WORKSPACE',
  'wave.video/project/MOVE_PROJECT_TO_OTHER_WORKSPACE_SUCCESS',
  'wave.video/project/MOVE_PROJECT_TO_OTHER_WORKSPACE_FAILURE'
)<MoveProjectToTeamParams, MoveProjectToTeamParams, ApiError>();
